// Community.tsx
import { 
  Box, 
  Flex, 
  Heading, 
  Text, 
  Button, 
  VStack, 
  HStack, 
  Avatar, 
  Badge, 
  IconButton,
  useDisclosure,
  useToast,
  Spinner,
  Center,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useBreakpointValue,
  Image,
  Grid
} from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import { useTranslation } from 'react-i18next'
import { FiEdit3, FiTrash2, FiMoreVertical, FiHeart, FiMessageCircle, FiBookmark, FiRefreshCw } from 'react-icons/fi'
import DefaultLayout from '../components/layouts/DefaultLayout'
import ImageViewerModal from '../components/community/ImageViewerModal'
import NicknameModal from '../components/community/NicknameModal'
import { 
  getCommunityPosts, 
  deleteCommunityPost, 
  likeCommunityPost, 
  dislikeCommunityPost,
  getUserPostLikeStatus,
  updateCommunityPost
} from '../services/apiService'

interface CommunityPost {
  id: number
  user_id: number
  title: string
  content: string
  view_count: number
  like_count: number
  dislike_count: number
  comment_count: number
  status: string
  category?: 'FREE' | 'PROFIT'
  image_urls?: string[]
  created_at: string
  updated_at: string
  user: {
    id: number
    username: string
    nickname?: string
    profile_image_url?: string
    grade?: string
    partner_count?: string | null
  }
}

const Community = () => {
  const navigate = useNavigate()
  const [auth] = useRecoilState(authState)
  const { t } = useTranslation()
  const toast = useToast()
  
  const [posts, setPosts] = useState<CommunityPost[]>([])
  const [filteredPosts, setFilteredPosts] = useState<CommunityPost[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [likedPosts, setLikedPosts] = useState<Set<number>>(new Set())
  const [dislikedPosts, setDislikedPosts] = useState<Set<number>>(new Set())
  const [selectedCategory, setSelectedCategory] = useState<'ALL' | 'FREE' | 'PROFIT'>('ALL')
  const [imageViewerOpen, setImageViewerOpen] = useState(false)
  const [viewerImages, setViewerImages] = useState<string[]>([])
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0)
  const [nicknameModalOpen, setNicknameModalOpen] = useState(false)
  
  const isMobile = useBreakpointValue({ base: true, md: false })

  // 게시글 목록 조회 (첫 페이지)
  const fetchPosts = async (category?: 'FREE' | 'PROFIT', reset = true) => {
    try {
      if (reset) {
        setLoading(true)
        setCurrentPage(1)
        setHasMore(true)
      } else {
        setLoadingMore(true)
      }

      const response = await getCommunityPosts({ 
        page: reset ? 1 : currentPage + 1, 
        pageSize: 20,
        category: category
      })
      
      const postsData = response.data.data.items
      const total = response.data.data.total
      const totalPages = response.data.data.totalPages
      
      console.log('API Response:', {
        currentPage: reset ? 1 : currentPage + 1,
        total,
        totalPages,
        itemsCount: postsData.length,
        hasMore: (reset ? 2 : currentPage + 2) <= totalPages
      })

      if (reset) {
        setPosts(postsData)
        setFilteredPosts(postsData)
        setCurrentPage(1)
      } else {
        setPosts(prev => [...prev, ...postsData])
        setFilteredPosts(prev => [...prev, ...postsData])
        setCurrentPage(prev => prev + 1)
      }

      // 더 이상 불러올 페이지가 없는지 확인
      const nextPage = reset ? 2 : currentPage + 2
      setHasMore(nextPage <= totalPages)

      // 로그인한 사용자의 좋아요/싫어요 상태 확인
      if (auth.id) {
        const userLikeStatuses = await Promise.all(
          postsData.map(async (post: CommunityPost) => {
            try {
              const status = await getUserPostLikeStatus(post.id)
              return { postId: post.id, ...status }
            } catch (error) {
              console.error(`Post ${post.id} like status error:`, error)
              return { postId: post.id, isLiked: false, isDisliked: false }
            }
          })
        )

        // 좋아요/싫어요 상태를 Set에 저장
        const newLikedPosts = new Set(likedPosts)
        const newDislikedPosts = new Set(dislikedPosts)

        userLikeStatuses.forEach(({ postId, isLiked, isDisliked }) => {
          if (isLiked) newLikedPosts.add(postId)
          if (isDisliked) newDislikedPosts.add(postId)
        })

        setLikedPosts(newLikedPosts)
        setDislikedPosts(newDislikedPosts)
      }
    } catch (error) {
      toast({
        title: '오류',
        description: '게시글을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  // 카테고리 필터링
  const filterPostsByCategory = (category: 'ALL' | 'FREE' | 'PROFIT') => {
    setSelectedCategory(category)
    if (category === 'ALL') {
      fetchPosts(undefined, true) // 전체 게시글 조회
    } else {
      fetchPosts(category, true) // 특정 카테고리 게시글 조회
    }
  }

  // 더 보기 버튼 클릭
  const handleLoadMore = () => {
    if (selectedCategory === 'ALL') {
      fetchPosts(undefined, false)
    } else {
      fetchPosts(selectedCategory, false)
    }
  }

  useEffect(() => {
    fetchPosts(undefined, true)
  }, [])

  const handleWritePost = () => {
    if (!auth.id) {
      toast({
        title: '로그인 필요',
        description: '글을 작성하려면 로그인이 필요합니다.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }
    
    // 닉네임이 없으면 닉네임 설정 모달 표시
    if (!auth.nickname) {
      setNicknameModalOpen(true)
      return
    }
    
    navigate('/community/write')
  }

  const handleEditPost = (post: CommunityPost) => {
    if (post.user_id !== auth.id) {
      toast({
        title: '권한 없음',
        description: '본인의 글만 수정할 수 있습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }
    navigate(`/community/${post.id}/edit`)
  }

  const handleDeletePost = async (post: CommunityPost) => {
    if (post.user_id !== auth.id) {
      toast({
        title: '권한 없음',
        description: '본인의 글만 삭제할 수 있습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }
    
    if (window.confirm('정말로 이 글을 삭제하시겠습니까?')) {
      try {
        await deleteCommunityPost(post.id)
        toast({
          title: '삭제 완료',
          description: '게시글이 삭제되었습니다.',
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        fetchPosts(selectedCategory === 'ALL' ? undefined : selectedCategory, true)
      } catch (error) {
        toast({
          title: '오류',
          description: '게시글 삭제에 실패했습니다.',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    }
  }

  const handleChangeCategory = async (post: CommunityPost) => {
    if (auth.isAdmin !== 1) {
      toast({
        title: '권한 없음',
        description: '관리자만 카테고리를 변경할 수 있습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }

    const newCategory = post.category === 'FREE' ? 'PROFIT' : 'FREE'
    const categoryName = newCategory === 'FREE' ? '자유게시판' : '수익인증'
    
    if (window.confirm(`이 게시글을 "${categoryName}"으로 변경하시겠습니까?`)) {
      try {
        await updateCommunityPost(post.id, {
          title: post.title,
          content: post.content,
          category: newCategory,
          image_urls: post.image_urls
        })
        
        toast({
          title: '카테고리 변경 완료',
          description: `게시글이 "${categoryName}"으로 변경되었습니다.`,
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        
        fetchPosts(selectedCategory === 'ALL' ? undefined : selectedCategory, true)
      } catch (error) {
        toast({
          title: '오류',
          description: '카테고리 변경에 실패했습니다.',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    }
  }

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return '방금 전'
    if (diffInMinutes < 60) return `${diffInMinutes}분 전`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}시간 전`
    return `${Math.floor(diffInMinutes / 1440)}일 전`
  }

  const handlePostClick = (postId: number) => {
    navigate(`/community/${postId}`)
  }

  const handleLikePost = async (postId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    
    if (!auth.id) {
      toast({
        title: '로그인 필요',
        description: '좋아요를 누르려면 로그인이 필요합니다.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    try {
      const response = await likeCommunityPost(postId)
      console.log('Like response:', response) // 디버깅용
      console.log('Like response.data:', response.data) // 디버깅용
      console.log('Like response.data.data:', response.data.data) // 디버깅용
      
      const action = (response.data as any)?.data?.action || (response.data as any)?.action
      console.log('Action:', action) // 디버깅용
      
      // 백엔드 응답에 따라 상태 업데이트
      if (action === 'like') {
        // 새로운 좋아요
        setLikedPosts(prev => new Set(prev).add(postId))
        setDislikedPosts(prev => {
          const newSet = new Set(prev)
          newSet.delete(postId)
          return newSet
        })
        
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { ...post, like_count: post.like_count + 1 }
            : post
        ))
      } else if (action === 'like_cancelled') {
        // 좋아요 취소
        setLikedPosts(prev => {
          const newSet = new Set(prev)
          newSet.delete(postId)
          return newSet
        })
        
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { ...post, like_count: Math.max(0, post.like_count - 1) }
            : post
        ))
      } else if (action === 'dislike_to_like') {
        // 싫어요에서 좋아요로 변경
        setLikedPosts(prev => new Set(prev).add(postId))
        setDislikedPosts(prev => {
          const newSet = new Set(prev)
          newSet.delete(postId)
          return newSet
        })
        
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { 
                ...post, 
                like_count: post.like_count + 1,
                dislike_count: Math.max(0, post.dislike_count - 1)
              }
            : post
        ))
      } else {
        // action이 없거나 예상하지 못한 경우, 게시글 목록을 다시 로드
        console.log('예상하지 못한 action:', action, '게시글 목록 다시 로드')
        fetchPosts(selectedCategory === 'ALL' ? undefined : selectedCategory, true)
      }
    } catch (error) {
      console.error('좋아요 처리 실패:', error)
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류'
      toast({
        title: '오류',
        description: `좋아요 처리에 실패했습니다: ${errorMessage}`,
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  const handleDislikePost = async (postId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    
    if (!auth.id) {
      toast({
        title: '로그인 필요',
        description: '싫어요를 누르려면 로그인이 필요합니다.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    try {
      const response = await dislikeCommunityPost(postId)
      console.log('Dislike response:', response) // 디버깅용
      console.log('Dislike response.data:', response.data) // 디버깅용
      console.log('Dislike response.data.data:', response.data.data) // 디버깅용
      
      const action = (response.data as any)?.data?.action || (response.data as any)?.action
      console.log('Dislike Action:', action) // 디버깅용
      
      // 백엔드 응답에 따라 상태 업데이트
      if (action === 'dislike') {
        // 새로운 싫어요
        setDislikedPosts(prev => new Set(prev).add(postId))
        setLikedPosts(prev => {
          const newSet = new Set(prev)
          newSet.delete(postId)
          return newSet
        })
        
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { ...post, dislike_count: post.dislike_count + 1 }
            : post
        ))
      } else if (action === 'dislike_cancelled') {
        // 싫어요 취소
        setDislikedPosts(prev => {
          const newSet = new Set(prev)
          newSet.delete(postId)
          return newSet
        })
        
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { ...post, dislike_count: Math.max(0, post.dislike_count - 1) }
            : post
        ))
      } else if (action === 'like_to_dislike') {
        // 좋아요에서 싫어요로 변경
        setDislikedPosts(prev => new Set(prev).add(postId))
        setLikedPosts(prev => {
          const newSet = new Set(prev)
          newSet.delete(postId)
          return newSet
        })
        
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { 
                ...post, 
                dislike_count: post.dislike_count + 1,
                like_count: Math.max(0, post.like_count - 1)
              }
            : post
        ))
      } else {
        // action이 없거나 예상하지 못한 경우, 게시글 목록을 다시 로드
        console.log('예상하지 못한 dislike action:', action, '게시글 목록 다시 로드')
        fetchPosts(selectedCategory === 'ALL' ? undefined : selectedCategory, true)
      }
    } catch (error) {
      console.error('싫어요 처리 실패:', error)
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류'
      toast({
        title: '오류',
        description: `싫어요 처리에 실패했습니다: ${errorMessage}`,
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  if (loading) {
    return (
      <DefaultLayout>
        <Center py={20}>
          <Spinner size="xl" />
        </Center>
      </DefaultLayout>
    )
  }

  return (
    <DefaultLayout>
      <Box px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        <Flex justify="space-between" align="center" mb={6}>
          <Heading as="h1" size="lg">
            커뮤니티
          </Heading>
          <Button
            bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            color="white"
            size={isMobile ? "sm" : "md"}
            onClick={handleWritePost}
            borderRadius="xl"
            px={isMobile ? 6 : 8}
            py={isMobile ? 4 : 5}
            fontSize={isMobile ? "sm" : "md"}
            fontWeight="bold"
            boxShadow="0 6px 20px rgba(102, 126, 234, 0.3)"
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
              bg: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
              _before: {
                left: '100%'
              }
            }}
            _active={{
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
            }}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            position="relative"
            overflow="hidden"
            _before={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
              transition: 'left 0.5s'
            }}
          >
            ✍️ 글쓰기
          </Button>
        </Flex>

        {/* 카테고리 필터 */}
        <HStack spacing={2} mb={6} justify="center">
          <Button
            size="sm"
            variant={selectedCategory === 'ALL' ? 'solid' : 'outline'}
            colorScheme={selectedCategory === 'ALL' ? 'blue' : 'gray'}
            onClick={() => filterPostsByCategory('ALL')}
            borderRadius="full"
            px={4}
          >
            전체
          </Button>
          <Button
            size="sm"
            variant={selectedCategory === 'FREE' ? 'solid' : 'outline'}
            colorScheme={selectedCategory === 'FREE' ? 'blue' : 'gray'}
            onClick={() => filterPostsByCategory('FREE')}
            borderRadius="full"
            px={4}
          >
            자유게시판
          </Button>
          <Button
            size="sm"
            variant={selectedCategory === 'PROFIT' ? 'solid' : 'outline'}
            colorScheme={selectedCategory === 'PROFIT' ? 'green' : 'gray'}
            onClick={() => filterPostsByCategory('PROFIT')}
            borderRadius="full"
            px={4}
          >
            수익인증
          </Button>
        </HStack>

        <VStack spacing={4} align="stretch">
          {filteredPosts.map((post) => (
            <Box
              key={post.id}
              bg="white"
              borderRadius="lg"
              border="1px"
              borderColor="gray.200"
              p={4}
              cursor="pointer"
              onClick={() => handlePostClick(post.id)}
            >
              <Flex justify="space-between" align="start" mb={3}>
                <HStack spacing={3}>
                  <Avatar
                    size="sm"
                    name={post.user.nickname || post.user.username}
                    src={post.user.profile_image_url || undefined}
                  />
                  <VStack align="start" spacing={0}>
                    <HStack spacing={2} align="center">
                      <Text fontSize="sm" fontWeight="medium">
                        {post.user.nickname || post.user.username}
                      </Text>
                      {/* 사용자 등급 뱃지 */}
                      {post.user.grade && (
                        <Badge
                          colorScheme={
                            post.user.grade === 'SECRET' ? 'purple' :
                            post.user.grade === 'VIP' ? 'yellow' :
                            post.user.grade === 'BASIC' ? 'blue' :
                            'gray'
                          }
                          variant="solid"
                          fontSize="xs"
                          fontWeight="bold"
                          px={2}
                          py={0.5}
                          borderRadius="md"
                        >
                          {post.user.grade}
                        </Badge>
                      )}
                      {/* 파트너 수 뱃지 */}
                      {post.user.partner_count && (
                        <Badge
                          colorScheme="green"
                          variant="outline"
                          fontSize="xs"
                          fontWeight="semibold"
                          px={2}
                          py={0.5}
                          borderRadius="md"
                        >
                          파트너 {post.user.partner_count}
                        </Badge>
                      )}
                    </HStack>
                    <Text fontSize="xs" color="gray.500">
                      {formatTimeAgo(post.created_at)}
                    </Text>
                  </VStack>
                </HStack>
                
                <HStack spacing={2}>
                  <Badge 
                    colorScheme={post.category === 'PROFIT' ? 'green' : 'blue'} 
                    variant="solid" 
                    fontSize="xs"
                    fontWeight="semibold"
                  >
                    {post.category === 'PROFIT' ? '💰 수익인증' : '💬 자유게시판'}
                  </Badge>
                  {(auth.id === post.user_id || auth.isAdmin === 1) && (
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<FiMoreVertical />}
                        variant="ghost"
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <MenuList>
                        {auth.id === post.user_id && (
                          <>
                            <MenuItem
                              icon={<FiEdit3 />}
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEditPost(post)
                              }}
                            >
                              수정
                            </MenuItem>
                            <MenuItem
                              icon={<FiTrash2 />}
                              color="red.500"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeletePost(post)
                              }}
                            >
                              삭제
                            </MenuItem>
                          </>
                        )}
                        {auth.isAdmin === 1 && (
                          <MenuItem
                            icon={<FiRefreshCw />}
                            color="blue.500"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleChangeCategory(post)
                            }}
                          >
                            {post.category === 'FREE' ? '수익인증으로 변경' : '자유게시판으로 변경'}
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  )}
                </HStack>
              </Flex>

              <VStack align="start" spacing={2} mb={4}>
                {/* 이미지 미리보기 */}
                {post.image_urls && post.image_urls.length > 0 && (
                  <Box w="100%" mb={3}>
                    <Grid 
                      templateColumns={
                        post.image_urls.length === 1 
                          ? "1fr" 
                          : post.image_urls.length === 2 
                          ? "1fr 1fr" 
                          : "1fr 1fr 1fr"
                      } 
                      gap={2}
                      maxW="300px"
                    >
                      {post.image_urls.slice(0, 3).map((imageUrl, index) => (
                        <Image
                          key={index}
                          src={imageUrl}
                          alt={`게시글 이미지 ${index + 1}`}
                          borderRadius="md"
                          objectFit="cover"
                          h={post.image_urls!.length === 1 ? "150px" : "80px"}
                          w="100%"
                          border="1px solid"
                          borderColor="gray.200"
                          cursor="pointer"
                          onClick={(e) => {
                            e.stopPropagation()
                            setViewerImages(post.image_urls!)
                            setViewerInitialIndex(index)
                            setImageViewerOpen(true)
                          }}
                          _hover={{ opacity: 0.8 }}
                        />
                      ))}
                    </Grid>
                    {post.image_urls!.length > 3 && (
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        +{post.image_urls!.length - 3}개 더
                      </Text>
                    )}
                  </Box>
                )}
                
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  noOfLines={2}
                  onClick={(e) => e.stopPropagation()}
                >
                  {post.title}
                </Text>
                <Text
                  fontSize="sm"
                  color="gray.600"
                  noOfLines={3}
                  onClick={(e) => e.stopPropagation()}
                >
                  {post.content.replace(/<[^>]*>/g, '')}
                </Text>
              </VStack>

              <HStack spacing={4}>
                <HStack 
                  spacing={1} 
                  cursor="pointer" 
                  _hover={{ opacity: 0.7 }}
                  onClick={(e) => handleLikePost(post.id, e)}
                >
                  <FiHeart 
                    size={16} 
                    color={likedPosts.has(post.id) ? "#e53e3e" : "gray"} 
                    fill={likedPosts.has(post.id) ? "#e53e3e" : "none"}
                  />
                  <Text fontSize="sm" color={likedPosts.has(post.id) ? "red.500" : "gray.600"}>
                    {post.like_count}
                  </Text>
                </HStack>
                <HStack 
                  spacing={1} 
                  cursor="pointer" 
                  _hover={{ opacity: 0.7 }}
                  onClick={(e) => handleDislikePost(post.id, e)}
                >
                  <Text fontSize="sm" color={dislikedPosts.has(post.id) ? "red.500" : "gray.600"}>
                    👎
                  </Text>
                  <Text fontSize="sm" color={dislikedPosts.has(post.id) ? "red.500" : "gray.600"}>
                    {post.dislike_count}
                  </Text>
                </HStack>
                <HStack spacing={1}>
                  <FiMessageCircle size={16} color="gray" />
                  <Text fontSize="sm" color="gray.600">
                    {post.comment_count}
                  </Text>
                </HStack>
              </HStack>
            </Box>
          ))}
        </VStack>

        {filteredPosts.length === 0 && (
          <Center py={20}>
            <VStack spacing={4}>
              <Text color="gray.500" fontSize="lg">
                {selectedCategory === 'ALL' && '아직 게시글이 없습니다.'}
                {selectedCategory === 'FREE' && '자유게시판에 게시글이 없습니다.'}
                {selectedCategory === 'PROFIT' && '수익인증 게시글이 없습니다.'}
              </Text>
              <Button colorScheme="blue" onClick={handleWritePost}>
                첫 번째 글 작성하기
              </Button>
            </VStack>
          </Center>
        )}

        {/* 더 보기 버튼 */}
        {filteredPosts.length > 0 && hasMore && (
          <Center py={8}>
            <Button
              onClick={handleLoadMore}
              isLoading={loadingMore}
              loadingText="로딩 중..."
              colorScheme="blue"
              variant="outline"
              size="lg"
              px={8}
              py={6}
              borderRadius="xl"
              _hover={{
                bg: "blue.50",
                borderColor: "blue.300"
              }}
            >
              📄 더 보기 ({filteredPosts.length}개 로드됨)
            </Button>
          </Center>
        )}

        {/* 더 이상 불러올 게시글이 없을 때 */}
        {filteredPosts.length > 0 && !hasMore && (
          <Center py={8}>
            <Text color="gray.500" fontSize="sm">
              모든 게시글을 불러왔습니다.
            </Text>
          </Center>
        )}
      </Box>

      <ImageViewerModal
        isOpen={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        images={viewerImages}
        initialIndex={viewerInitialIndex}
      />
      
      <NicknameModal
        isOpen={nicknameModalOpen}
        onClose={() => setNicknameModalOpen(false)}
        onSuccess={() => {
          // 닉네임 설정 후 사용자 정보 새로고침
          window.location.reload()
        }}
      />
    </DefaultLayout>
  )
}

export default Community
